"use client"

import { PageHeader } from "@/components/graphite/page-header"
import { useCommunity } from "@/hooks/use-community"
import { cn } from "@/lib/utils"
import { BucketManager } from "@/components/community/bucket-manager"
import { ContactSelector } from "@/components/community/contact-selector"
import { MessageApproval } from "@/components/community/message-approval"
import { CompaniesTab } from "@/components/community/companies-tab"
import { createRoute } from "@tanstack/react-router"
import MainAppRoute from "@/rootRoutes/MainApp"



export const CommunityViewRoute = createRoute({
  getParentRoute: () => MainAppRoute,
  path: "/community/$view",
  component: CommunityPage,
  validateSearch: (search: Record<string, unknown>) => {
    const rawCreateContact = search.createContact
    const rawCreateCompany = search.createCompany
    const createContact =
      rawCreateContact === true ||
      rawCreateContact === "true" ||
      rawCreateContact === "1"

    const createCompany =
      rawCreateCompany === true ||
      rawCreateCompany === "true" ||
      rawCreateCompany === "1"

    const companyTab =
      search.companyTab === "contacts" || search.companyTab === "jobs"
        ? search.companyTab
        : undefined

    return {
      companyId: typeof search.companyId === "string" ? search.companyId : undefined,
      companyTab,
      createContact: createContact ? true : undefined,
      createCompany: createCompany ? true : undefined,
    }
  },
})

export default CommunityViewRoute



/** The four views, in the order the tabs read. */
const VIEWS = [
  { id: "companies", label: "Companies" },
  { id: "contacts", label: "Contacts" },
  { id: "buckets", label: "Buckets" },
  { id: "approvals", label: "Message Approvals" },
] as const

const VALID_VIEWS = VIEWS.map((entry) => entry.id)
type CommunityView = (typeof VIEWS)[number]["id"]

function CommunityPage() {
  const navigate = CommunityViewRoute.useNavigate()
  const { view } = CommunityViewRoute.useParams()

  const community = useCommunity()

  const activeView: CommunityView =
    VALID_VIEWS.includes(view as CommunityView) ? (view as CommunityView) : "companies"


    const switchTab = (next: string) => {
      navigate({
        to: `/community/${next}`,
        replace: true,
        search: (prev: Record<string, unknown>) => {
          const out: Record<string, unknown> = { ...(prev ?? {}) }
    
          // leaving companies -> clear company context
          if (next !== "companies") {
            delete out.companyId
            delete out.companyTab
            delete out.createCompany
          }
    
          // leaving contacts -> clear create-contact modal
          if (next !== "contacts") {
            delete out.createContact
          }
    
          return out
        },
      })
    }
    

  return (
    <div className="@container flex h-full min-h-0 flex-col">
      <PageHeader
        title="Community"
        description="Stay close to your people; candidates and partners alike"
        actions={
          /*
            17a leads with these two: the size of the population, and how much
            of it has gone quiet. Amber is the canvas's "needs you", and the
            second figure only appears when there is something behind it.
          */
          <>
            <span className="hidden font-mono text-[11px] text-muted-foreground @2xl:inline">
              {community.isCounting || community.isCountsError
                ? "—"
                : `${community.totalContacts.toLocaleString()} contacts`}
            </span>
            {community.pastDue > 0 && (
              <span className="font-mono text-[11px] text-attention">
                {community.pastDue} past due
              </span>
            )}
          </>
        }
      >
        {/* Underline tabs, as every other page in the product has. The pill
            group here was the last `TabsList` left on a page header. */}
        <div className="mt-4 flex gap-5 overflow-x-auto border-b">
          {VIEWS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => switchTab(entry.id)}
              className={cn(
                "-mb-px inline-flex items-baseline whitespace-nowrap border-b-2 px-0.5 py-2 text-[12.5px] transition-colors",
                activeView === entry.id
                  ? "border-primary font-medium text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {entry.label}
              <span className="ml-1.5 font-mono text-[10px] text-muted-foreground">
                {community.tabCounts[entry.id]}
              </span>
            </button>
          ))}
        </div>
      </PageHeader>

      <div className="min-h-0 flex-1 overflow-y-auto p-4 @2xl:px-[26px] @2xl:pb-[26px] @2xl:pt-[18px]">
        {activeView === "companies" && <CompaniesTab />}
        {activeView === "contacts" && <ContactSelector />}
        {activeView === "buckets" && <BucketManager searchTerm="" />}
        {activeView === "approvals" && <MessageApproval />}
      </div>
    </div>
  )
}
